import {
    HelpMenuAction,
    HelpMenuCategory,
} from '../../models/helpActions';
import { MenuAction } from '../../models/menuAction';
import store from '../../store/store';
import { accountAndTenantsStore } from '../../utils/accountAndTenants/accountAndTenants';
import { getLanguage } from '../../utils/languageHelper';
import { t } from '../../utils/localization/loc';

export const BuiltInHelpItems = {
    Forum: 'help-menu-forum',
    Academy: 'help-menu-uipath-academy',
    YouTube: 'help-menu-youtube-tutorials',
    ReleaseNotes: 'help-menu-release-notes',
    Support: 'help-menu-support',
};

export const BuiltInHelpLinks = {
    Academy: {
        ja: 'https://academy.uipath.com/ja',
        'zh-cn': 'https://academy.uipath.com/cn',
        'zh-tw': 'https://academy.uipath.com/cn',
        en: 'https://academy.uipath.com',
    },
    Forum: 'https://forum.uipath.com',
    ReleaseNotes: {
        'zh-cn': 'https://docs.uipath.com/zh-CN/releasenotes/other/latest',
        'zh-tw': 'https://docs.uipath.com/zh-CN/releasenotes/other/latest',
        fr: 'https://docs.uipath.com/fr/releasenotes/other/latest',
        de: 'https://docs.uipath.com/de/releasenotes/other/latest',
        ja: 'https://docs.uipath.com/ja/releasenotes/other/latest',
        es: 'https://docs.uipath.com/es/releasenotes/other/latest',
        'es-mx': 'https://docs.uipath.com/es/releasenotes/other/latest',
        'pt-br': 'https://docs.uipath.com/pt-BR/releasenotes/other/latest',
        pt: 'https://docs.uipath.com/pt-BR/releasenotes/other/latest',
        en: 'https://docs.uipath.com/releasenotes/other/latest',
    },
    Support: {
        ja: 'https://www.uipath.com/ja/company/customer-support',
        en: 'https://uipath.com/support',
    },
    YouTube: 'https://www.uipath.com/learning/video-tutorials',
};

const builtInActions = (locale: string): HelpMenuAction[] => [
    {
        type: 'copy',
        id: 'supportId',
        category: 'supportId',
        text: accountAndTenantsStore.get('organization')?.licenseCode ?? t('unlicensed', getLanguage(store)),
        order: 0,
    },
    {
        type: 'link',
        category: 'help',
        text: t(BuiltInHelpItems.Forum, locale),
        href: BuiltInHelpLinks.Forum,
        order: 1,
    },
    {
        type: 'link',
        category: 'training',
        text: t(BuiltInHelpItems.Academy, locale),
        href: BuiltInHelpLinks.Academy[locale] || BuiltInHelpLinks.Academy.en,
        order: 0,
    },
    {
        type: 'link',
        category: 'training',
        text: t(BuiltInHelpItems.YouTube, locale),
        href: BuiltInHelpLinks.YouTube,
        order: 1,
    },
    {
        type: 'link',
        category: 'resources',
        text: t(BuiltInHelpItems.ReleaseNotes, locale),
        href: BuiltInHelpLinks.ReleaseNotes[locale] || BuiltInHelpLinks.ReleaseNotes.en,
        order: 1,
    },
    {
        type: 'link',
        category: 'resources',
        text: t(BuiltInHelpItems.Support, locale),
        href: BuiltInHelpLinks.Support[locale] || BuiltInHelpLinks.Support.en,
        order: 2,
    },
];

/**
 * `buildHelpMenu(helpMenuActions)` returns a `MenuAction[]`, ready to be plugged into `portal-menu`
 *
 * The `helpMenuActions` are added to the default actions, and are added to appropriate sections based on their category.
 */
export const buildHelpMenu = (helpActions: HelpMenuAction[], locale: string): MenuAction[] => {
    let localizedBuiltInActions = builtInActions(locale);
    const builtInTokens = Object.values(BuiltInHelpItems);

    const overrideHelpItems = helpActions
        .filter(helpItem => builtInTokens.includes(helpItem.text.toLowerCase()));
    const overrideHelpItemsText = overrideHelpItems
        .map(helpItem => t(helpItem.text, locale));

    overrideHelpItems.forEach((helpItem, i) => helpItem.text = overrideHelpItemsText[i]);
    localizedBuiltInActions = localizedBuiltInActions
        .filter(builtInAction => !overrideHelpItemsText.includes(builtInAction.text));

    const allHelpActions = [ ...helpActions, ...localizedBuiltInActions ];
    const categorizedMenuItems = categorizeMenuItems(allHelpActions);
    return createMenuActions(categorizedMenuItems, locale);
};

/**
 * Build a record, categorizing help menu actions into individual buckets
 */
const categorizeMenuItems = (helpActions: HelpMenuAction[]): Record<HelpMenuCategory, MenuAction[]> => {
    // Pre-sort the help menu actions so that the 'order' is respected
    const sortedHelpActions = [ ...helpActions ].sort((helpActionA, helpActionB) => {
        const orderA = HelpMenuAction.sortOrder(helpActionA);
        const orderB = HelpMenuAction.sortOrder(helpActionB);
        return orderA - orderB;
    });

    return sortedHelpActions.reduce((acc, cur) => {
        const category = cur.category ? cur.category : 'training';
        return {
            ...acc,
            [category]: [ ...acc[category], helpMenuActionToMenuAction(cur) ],
        };
    }, {
        'supportId': [],
        'help': [],
        'training': [],
        'resources': [],
    });
};

/**
 * Converts a `HelpMenuAction` -> `MenuAction`
 */
const helpMenuActionToMenuAction = (helpMenuAction: HelpMenuAction): MenuAction => {
    switch (helpMenuAction.type) {
        case 'link':
            return {
                name: helpMenuAction.text,
                type: 'link',
                href: helpMenuAction.href,
            };
        case 'copy':
            return {
                name: helpMenuAction.text,
                type: 'copy',
                id: helpMenuAction.id,
            };
        case 'button':
            return {
                name: helpMenuAction.text,
                type: 'text',
                id: helpMenuAction.id,
            };
    }
};

/**
 * Converts a categorized set of HelpMenuActions into a nice menu with dividers and headers.
 */
const createMenuActions = (helpMenuItems: Record<HelpMenuCategory, MenuAction[]>, locale: string): MenuAction[] => {
    return [
        {
            type: 'header',
            text: t('Support Id', locale),
        },
        ...helpMenuItems['supportId'],
        { type: 'divider' },
        {
            type: 'header',
            text: t('help', locale),
        },
        ...helpMenuItems['help'],
        { type: 'divider' },
        {
            type: 'header',
            text: t('help-menu-training-tutorials', locale),
        },
        ...helpMenuItems['training'],
        { type: 'divider' },
        {
            type: 'header',
            text: t('help-menu-resources', locale),
        },
        ...helpMenuItems['resources'],
    ];
};
